(function() {
  'use strict';

  // Utility functions
  const utils = {
    debounce(func, delay) {
      let timeoutId;
      return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    },

    normalizeText(text) {
      return text.toLowerCase().trim().replace(/[^\w\s]/g, '');
    },

    getFirstLetter(text) {
      const letter = text.charAt(0).toUpperCase();
      return /^[A-Z]$/.test(letter) ? letter : '0-9';
    },

    groupByLetter(designers) {eventBus
      const groups = {};
      designers.forEach(designer => {
        const letter = this.getFirstLetter(designer.name);
        if (!groups[letter]) {
          groups[letter] = [];
        }
        groups[letter].push(designer);
      });
      
      // Sort designers within each group
      Object.keys(groups).forEach(letter => {
        groups[letter].sort((a, b) => a.name.localeCompare(b.name));
      });
      
      return groups;
    }
  };

  // Event system for communication between components
  class EventBus {
    constructor() {
      this.events = {};
    }

    on(event, callback) {
      if (!this.events[event]) {
        this.events[event] = [];
      }
      this.events[event].push(callback);
    }

    emit(event, data) {
      if (this.events[event]) {
        this.events[event].forEach(callback => callback(data));
      }
    }

    off(event, callback) {
      if (this.events[event]) {
        this.events[event] = this.events[event].filter(cb => cb !== callback);
      }
    }
  }

  // Global event bus instance
  const eventBus = new EventBus();

  // Base class for designer components
  class DesignerComponent extends HTMLElement {
    constructor() {
      super();
      this.eventBus = eventBus;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    init() {
      // Override in subclasses
    }

    cleanup() {
      // Override in subclasses
    }

    getDesignersData() {
      const section = this.closest('.designers-list');
      if (!section) {
        console.warn('Designers section not found');
        return [];
      }
      
      const dataAttr = section.getAttribute('data-designers');
      if (!dataAttr) {
        console.warn('No designers data attribute found');
        return [];
      }
      
      try {
        // First decode HTML entities if needed
        const decodedData = dataAttr.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&");
        const parsedData = JSON.parse(decodedData);
        
        // Validate data structure
        if (!Array.isArray(parsedData)) {
          console.error('Designers data should be an array');
          return [];
        }
        
        // Validate each designer object
        const validDesigners = parsedData.filter(designer => {
          if (!designer || typeof designer !== 'object') return false;
          if (!designer.name || !designer.url) return false;
          return true;
        });
        
        if (validDesigners.length !== parsedData.length) {
          console.warn('Some designer entries were invalid and filtered out');
        }
        
        return validDesigners;
      } catch (e) {
        console.error('Error parsing designers data:', e);
        console.log('Raw data:', dataAttr);
        
        // Fallback to default data
        return [
          {"name": "Max Mara", "url": "/collections/max-mara"},
          {"name": "Dolce & Gabbana", "url": "/collections/dolce-gabbana"},
          {"name": "Brunello Cucinelli", "url": "/collections/brunello-cucinelli"}
        ];
      }
    }
  }

  // Search component
  class DesignersSearch extends DesignerComponent {
    init() {
      this.input = this.querySelector('.designers-list__search-input');
      if (!this.input) return;

      this.searchTerm = '';
      this.debouncedSearch = utils.debounce(this.handleSearch.bind(this), 300);
      
      this.input.addEventListener('input', this.debouncedSearch);
      this.input.addEventListener('keydown', this.handleKeydown.bind(this));
      
      // Listen for clear search events
      this.eventBus.on('search:clear', this.clearSearch.bind(this));
    }

    handleSearch() {
      this.searchTerm = this.input.value.trim();
      
      // Update visual state
      const wrapper = this.closest('.designers-list__search-wrapper');
      if (wrapper) {
        if (this.searchTerm) {
          wrapper.classList.add('designers-list__search--active');
        } else {
          wrapper.classList.remove('designers-list__search--active');
        }
      }
      
      this.eventBus.emit('search:changed', this.searchTerm);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.clearSearch();
      }
    }

    clearSearch() {
      this.input.value = '';
      this.searchTerm = '';
      
      // Update visual state
      const wrapper = this.closest('.designers-list__search-wrapper');
      if (wrapper) {
        wrapper.classList.remove('designers-list__search--active');
      }
      
      this.eventBus.emit('search:changed', '');
    }

    cleanup() {
      if (this.input) {
        this.input.removeEventListener('input', this.debouncedSearch);
        this.input.removeEventListener('keydown', this.handleKeydown);
      }
      this.eventBus.off('search:clear', this.clearSearch);
    }
  }

  // Filter component
  class DesignersFilter extends DesignerComponent {
    init() {
      this.designers = this.getDesignersData();
      this.activeFilter = 'all';
      this.availableLetters = this.getAvailableLetters();
      
      this.render();
      this.attachEventListeners();
      
      // Listen for search changes to update available filters
      this.eventBus.on('search:changed', this.handleSearchChange.bind(this));
      this.eventBus.on('designers:filtered', this.updateAvailableLetters.bind(this));
    }

    getAvailableLetters() {
      const letters = new Set();
      this.designers.forEach(designer => {
        letters.add(utils.getFirstLetter(designer.name));
      });
      
      const sortedLetters = Array.from(letters).sort((a, b) => {
        if (a === '0-9') return 1;
        if (b === '0-9') return -1;
        return a.localeCompare(b);
      });
      
      return sortedLetters;
    }

    render() {
      // Keep the ALL button
      const allButton = this.querySelector('[data-letter="all"]');
      
      // Clear other buttons
      this.querySelectorAll('.designers-list__filter-btn:not([data-letter="all"])').forEach(btn => btn.remove());
      
      // Add letter buttons
      this.availableLetters.forEach(letter => {
        const button = document.createElement('button');
        button.className = 'designers-list__filter-btn';
        button.setAttribute('data-letter', letter);
        button.textContent = letter;
        button.type = 'button';
        this.appendChild(button);
      });
    }

    attachEventListeners() {
      this.addEventListener('click', this.handleFilterClick.bind(this));
    }

    handleFilterClick(event) {
      const button = event.target.closest('.designers-list__filter-btn');
      if (!button) return;

      const letter = button.getAttribute('data-letter');
      this.setActiveFilter(letter);
    }

    setActiveFilter(letter) {
      if (this.activeFilter === letter) return;

      // Update active button
      this.querySelectorAll('.designers-list__filter-btn').forEach(btn => {
        btn.classList.remove('designers-list__filter-btn--active');
      });
      
      const activeButton = this.querySelector(`[data-letter="${letter}"]`);
      if (activeButton) {
        activeButton.classList.add('designers-list__filter-btn--active');
      }

      this.activeFilter = letter;
      this.eventBus.emit('filter:changed', letter);
    }

    handleSearchChange(searchTerm) {
      // Don't auto-switch to "all" - let user keep their letter filter during search
      // This allows searching within a specific letter group
    }

    updateAvailableLetters(filteredDesigners) {
      const newLetters = new Set();
      filteredDesigners.forEach(designer => {
        newLetters.add(utils.getFirstLetter(designer.name));
      });
      
      // Update button states - only show visual feedback, don't disable
      this.querySelectorAll('.designers-list__filter-btn').forEach(btn => {
        const letter = btn.getAttribute('data-letter');
        
        const isAvailable = newLetters.has(letter);
        // Only change opacity, keep buttons enabled for switching
        btn.style.opacity = isAvailable ? '1' : '0.6';
        if (newLetters.size > 1 && btn.getAttribute('data-letter') === 'all') btn.style.opacity = '1';
        btn.disabled = false; // Always keep buttons enabled
      });
    }

    cleanup() {
      this.removeEventListener('click', this.handleFilterClick);
      this.eventBus.off('search:changed', this.handleSearchChange);
      this.eventBus.off('designers:filtered', this.updateAvailableLetters);
    }
  }

  // Grid component
  class DesignersGrid extends DesignerComponent {
    init() {
      this.designers = this.getDesignersData();
      this.filteredDesigners = [...this.designers];
      this.currentFilter = 'all';
      this.currentSearch = '';
      
      this.eventBus.on('search:changed', this.handleSearchChange.bind(this));
      this.eventBus.on('filter:changed', this.handleFilterChange.bind(this));
      
      // Initial render
      this.updateDisplay();
    }

    handleSearchChange(searchTerm) {
      this.currentSearch = searchTerm;
      this.updateFilteredDesigners();
    }

    handleFilterChange(filter) {
      this.currentFilter = filter;
      this.updateFilteredDesigners();
    }

    updateFilteredDesigners() {
      let filtered = [...this.designers];
      
      // Apply search filter
      if (this.currentSearch) {
        const normalizedSearch = utils.normalizeText(this.currentSearch);
        filtered = filtered.filter(designer => 
          utils.normalizeText(designer.name).includes(normalizedSearch)
        );
      }
      
      // Apply letter filter
      if (this.currentFilter !== 'all') {
        filtered = filtered.filter(designer => 
          utils.getFirstLetter(designer.name) === this.currentFilter
        );
      }
      
      this.filteredDesigners = filtered;
      this.eventBus.emit('designers:filtered', filtered);
      this.updateDisplay();
    }

    updateDisplay() {
      // Clear current content
      this.innerHTML = '';
      
      
      if (this.filteredDesigners.length === 0) {
        this.renderEmpty();
        return;
      }
      
      // Always render grouped results to maintain alphabetical organization
      this.renderGroupedResults();
    }

    renderGroupedResults() {
      const groups = utils.groupByLetter(this.filteredDesigners);
      const sortedLetters = Object.keys(groups).sort((a, b) => {
        if (a === '0-9') return 1;
        if (b === '0-9') return -1;
        return a.localeCompare(b);
      });
      
      sortedLetters.forEach(letter => {
        const group = this.createLetterGroup(letter, groups[letter]);
        this.appendChild(group);
      });
    }

    createLetterGroup(letter, designers) {
      const group = document.createElement('div');
      group.className = 'designers-list__letter-group designers-list__letter-group--visible';
      
      const header = document.createElement('div');
      header.className = 'designers-list__letter-group-header';
      
      const title = document.createElement('h2');
      title.className = 'designers-list__letter-title';
      title.textContent = letter;
      
      const content = document.createElement('div');
      content.className = 'designers-list__letter-content';
      
      const grid = document.createElement('div');
      grid.className = 'designers-list__designers-grid';
      
      designers.forEach(designer => {
        const link = this.createDesignerLink(designer);
        grid.appendChild(link);
      });
      
      content.appendChild(grid);
      header.appendChild(title);
      header.appendChild(content);
      group.appendChild(header);
      
      return group;
    }

    createDesignerLink(designer) {
      const link = document.createElement('a');
      let gender = window.gender === "woman" ? "female" : "male";
      let designerName = designer.url.split("/")

      link.className = 'designers-list__designer-link';
      link.href = `/a/search/${designerName[2]}?filter_custom.gender=${gender}`;
      link.textContent = designer.name;
      link.setAttribute('aria-label', `View ${designer.name} collection`);
      
      return link;
    }

    renderEmpty() {
      const empty = document.createElement('div');
      empty.className = 'designers-list__empty';
      
      const title = document.createElement('h3');
      title.className = 'designers-list__empty-title';
      title.textContent = 'No designers found';
      
      const text = document.createElement('p');
      text.className = 'designers-list__empty-text';
      text.textContent = this.currentSearch 
        ? `No designers match "${this.currentSearch}". Try a different search term.`
        : 'No designers available for the selected filter.';
      
      empty.appendChild(title);
      empty.appendChild(text);
      this.appendChild(empty);
    }

    cleanup() {
      this.eventBus.off('search:changed', this.handleSearchChange);
      this.eventBus.off('filter:changed', this.handleFilterChange);
    }
  }

  // Register custom elements
  if (!customElements.get('designers-search')) {
    customElements.define('designers-search', DesignersSearch);
  }
  
  if (!customElements.get('designers-filter')) {
    customElements.define('designers-filter', DesignersFilter);
  }
  
  if (!customElements.get('designers-grid')) {
    customElements.define('designers-grid', DesignersGrid);
  }

  // Initialize section functionality
  function initDesignersList() {
    const sections = document.querySelectorAll('.designers-list');
    
    sections.forEach(section => {
      // Check if already initialized
      if (section.hasAttribute('data-initialized')) return;
      section.setAttribute('data-initialized', 'true');
      
      // Debug: Check if data is available
      const dataAttr = section.getAttribute('data-designers');
      if (!dataAttr) {
        console.warn('Designers List: No data-designers attribute found. Please check section configuration.');
      } else {
        console.log('Designers List: Section initialized with data length:', dataAttr.length);
      }
      
      // Add any section-level initialization here
      const loadingElement = section.querySelector('.designers-list__loading');
      if (loadingElement) {
        // Hide loading after components are initialized
        setTimeout(() => {
          loadingElement.style.display = 'none';
        }, 100);
      }
    });
  }

  // Initialize on DOM ready and section load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDesignersList);
  } else {
    initDesignersList();
  }

  // Shopify section reload support
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('.designers-list')) {
      initDesignersList();
    }
  });

  // Export for potential external use
  window.DesignersListComponents = {
    DesignersSearch,
    DesignersFilter, 
    DesignersGrid,
    utils,
    eventBus
  };

})();
